import type { CityConfig } from "@/pages/city-template/types";

export const westBendConfig: CityConfig = {
  city: "West Bend",
  state: "Wisconsin",
  stateAbbr: "WI",
  slug: "west-bend",
  route: "/west-bend",
  title: "Emergency & Expedited Passport Services West Bend WI | Fast Passport Center",
  metaDescription: "Emergency and expedited passport services in West Bend, WI — as fast as 24 hours. No West Bend passport agency. Fast Passport Center couriers to the Chicago Passport Agency at 230 S Dearborn St, Suite 380, about 130 miles south via US-45 and I-94. A+ BBB rated. Serving Washington County families, West Bend Mutual Insurance professionals, and Kettle Moraine outdoor enthusiasts.",
  h1: "Emergency & Expedited Passport Services in West Bend, WI",
  heroSubtitle: "West Bend is a thriving Washington County community known for the Kettle Moraine State Forest, the Milwaukee River, and a strong manufacturing and insurance sector. Fast Passport Center's registered couriers deliver your documents to the Chicago Passport Agency at 230 S Dearborn St, Suite 380 so you get a new or renewed passport as fast as 24 hours — whether you're a West Bend Mutual Insurance professional traveling internationally, a Washington County family planning a European vacation, or a Kettle Moraine outdoor enthusiast heading abroad.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=West%20Bend%20Wisconsin%20downtown%20with%20historic%20Washington%20County%20Courthouse%20and%20the%20Milwaukee%20River%20running%20through%20the%20city%20during%20golden%20hour%20sunlight%2C%20charming%20midwestern%20main%20street%20with%20local%20businesses%20and%20autumn%20foliage%2C%20wide%20angle%20professional%20photography&width=1800&height=700&seq=west-bend-wi-hero-001&orientation=landscape",
  heroImageAlt: "West Bend Wisconsin downtown with Washington County Courthouse and Milwaukee River at golden hour",
  geoPosition: "43.4253;-88.1834",
  geoPlacename: "West Bend, Wisconsin",
  lastModified: "2026-06-06",
  regionalAgency: {
    name: "Chicago Passport Agency",
    address: "230 S Dearborn St, Suite 380, Chicago, IL 60604",
    distance: "Approximately 130 miles from West Bend — about 2 hours 10 minutes via US-45 and I-94",
  },
  nearbyCitiesZips: [
    { name: "West Bend", zips: "53095, 53090" },
    { name: "Germantown", zips: "53022" },
    { name: "Menomonee Falls", zips: "53051" },
    { name: "Hartford", zips: "53027" },
    { name: "Jackson", zips: "53037" },
    { name: "Slinger", zips: "53086" },
    { name: "Richfield", zips: "53076" },
    { name: "Kewaskum", zips: "53040" },
    { name: "Hubertus", zips: "53033" },
    { name: "Cedarburg", zips: "53012" },
    { name: "Port Washington", zips: "53074" },
    { name: "Grafton", zips: "53024" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Courier delivery to Chicago Passport Agency — 130 miles from West Bend via US-45" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-map-pin-2-line", title: "Washington County Hub", desc: "Serving West Bend, Germantown, Menomonee Falls, and all Washington County" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your West Bend case from start to finish" },
  ],
  internalLinks: [
    { name: "Emergency passport in Chicago IL", route: "/chicago" },
    { name: "Expedited passport in Milwaukee WI", route: "/milwaukee" },
    { name: "Urgent passport in West Bend WI", route: "/west-bend" },
    { name: "Wisconsin passport hub", route: "/wisconsin" },
    { name: "Fast passport in Waukesha WI", route: "/waukesha" },
    { name: "Expedited passport in Madison WI", route: "/madison-wi" },
    { name: "Emergency passport in Racine WI", route: "/racine" },
  ],
  fpcOfficeAddress: "230 S Dearborn St, Suite 380, Chicago, IL 60604",
  fpcOfficeNote: "Fast Passport Center serves West Bend remotely via secure courier. Our registered couriers hand-deliver your documents to the Chicago Passport Agency at 230 S Dearborn St, Suite 380 — approximately 130 miles south. West Bend's location in Washington County provides direct access to US-45 and I-41 corridors south toward Chicago, making overnight courier routing highly efficient. Our dedicated agents ensure Washington County travelers never need to make the drive to the Loop.",
  fpcOfficeDistance: "Serving all of Washington County",
  serviceAreaIntro: "Fast Passport Center provides emergency and fast passport renewal services throughout West Bend, Germantown, Menomonee Falls, Hartford, and the entire Washington County area. From West Bend Mutual Insurance professionals traveling internationally to Washington County families planning overseas vacations, we help Kettle Moraine area travelers get their passports fast.",
  nearbyCitiesIntro: "We serve West Bend and nearby communities including Germantown, Menomonee Falls, Hartford, Jackson, Slinger, Kewaskum, and the entire Washington County area.",
  howItWorksIntro: "Most West Bend travelers complete everything online and ship documents overnight. At 130 miles from Chicago, Fast Passport Center provides fast courier turnaround for Washington County residents — eliminating the 2-hour-plus drive to the Chicago Passport Agency.",
  mistakesIntro: "West Bend travelers, including West Bend Mutual Insurance professionals and Washington County families planning international trips, face unique passport challenges. Here are the most common mistakes we see.",
  testimonialsIntro: "West Bend and Washington County travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in West Bend? Here are the most common ones we hear from Washington County travelers.",
  statePageRoute: "/states/wisconsin",
  statePageName: "Wisconsin",
  dmvWebsite: "https://wisconsindot.gov/Pages/dmv/license-drvs/how-to-apply.aspx",
  dmvLabel: "Wisconsin DMV — Official Website",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "No drive or appointment needed",
        "Ideal for West Bend Mutual Insurance international travel",
        "Perfect for Washington County family vacations",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Faster than self-service USPS",
        "Good for planned travel from MKE",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: [
        "No courier access — standard mail only",
        "No document review — errors cause rejection",
        "In-person clerk appointment required",
        "Workload shifts with government shutdowns",
        "No dedicated agent — you are on your own",
        "Updates only by physical mail letter",
        "Cannot upgrade speed after submission",
        "Not suitable if travel is within 10 weeks",
      ],
      highlight: false,
      muted: true,
    },
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: [
        "Appointment required — no walk-ins at 230 S Dearborn St, Suite 380",
        "Life-or-death emergencies only",
        "130 miles to Chicago (2 hours 10 minutes via US-45/I-94)",
        "Still requires pre-scheduled NPIC appointment",
      ],
      highlight: false,
      emergency: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "West Bend is 130 miles from the Chicago Passport Agency — our courier service saves Washington County travelers the drive to the Loop.",
      bullets: ["Washington County coverage", "24–48 hour turnaround available", "No appointment or drive needed"],
      badge: "Fastest in Washington County",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for West Bend adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No acceptance clerk visit for most renewals", "Mail-in or courier options", "Serving West Bend and Washington County"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support in Washington County.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Fast replacement for West Bend and Washington County residents with DS-64 form guidance.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement via Chicago agency"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support in Washington County.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport name change service after marriage, divorce, or legal changes for Washington County residents.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport West Bend WI", description: "Urgent passport processing for West Bend, WI travelers via Chicago Passport Agency courier — 130 miles from West Bend." },
    { name: "Passport Renewal West Bend WI", description: "Fast passport renewal for West Bend and Washington County, Wisconsin residents." },
    { name: "New Passport West Bend WI", description: "First-time passport application assistance for West Bend and Washington County area residents." },
    { name: "Lost Passport West Bend WI", description: "Passport replacement for lost or stolen passports for West Bend and Washington County residents." },
    { name: "Child Passport West Bend WI", description: "Minor passport services for West Bend and Washington County families." },
    { name: "Name Change Passport West Bend WI", description: "Passport name change service for West Bend, WI residents." },
  ],
  testimonials: [
    {
      name: "Jennifer T.",
      location: "West Bend Mutual Insurance",
      rating: 5,
      text: "I'm a claims adjuster at West Bend Mutual and got called to an international insurance conference in London with two weeks notice. My passport had expired four months prior. Fast Passport Center expedited my renewal in 3 business days. I didn't miss a single session.",
      service: "Emergency Renewal",
    },
    {
      name: "Mark & Susan P.",
      location: "West Bend, WI",
      rating: 5,
      text: "Washington County couple planning our 30th anniversary trip to Italy. We had 5 weeks and both needed renewals. Fast Passport Center handled both in 7 business days. The Chicago courier service was seamless — so much easier than driving to the Chicago Loop ourselves.",
      service: "Dual Renewal",
    },
    {
      name: "Kevin D.",
      location: "Germantown, WI",
      rating: 5,
      text: "Germantown resident who needed a passport for a last-minute business trip to Toronto. Realized my passport was missing just 8 days before departure. Fast Passport Center helped with the DS-64 form and got my replacement couriered through Chicago in 5 business days.",
      service: "Lost Passport Replacement",
    },
    {
      name: "Rachel H.",
      location: "Hartford, WI",
      rating: 5,
      text: "First-time passport applicant for a family trip to Ireland. Fast Passport Center walked me through every step of the DS-11 process and directed me to the Washington County Clerk for my in-person visit. Had my passport in 8 business days — the whole family made the trip.",
      service: "First-Time Passport",
    },
  ],
  facilities: [
    {
      id: "wb-1",
      name: "West Bend Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "607 E Economy Ave",
      city: "West Bend",
      state: "WI",
      zip: "53095",
      phone: "(262) 334-3981",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "West Bend — Downtown area",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Primary acceptance facility for West Bend and Washington County residents. Appointment required. Located near the Milwaukee River.",
      icon: "ri-mail-line",
    },
    {
      id: "wb-2",
      name: "Washington County Clerk Office",
      type: "County Clerk Passport Acceptance",
      address: "432 E Washington St",
      city: "West Bend",
      state: "WI",
      zip: "53095",
      phone: "(262) 335-4301",
      hours: "Mon–Fri: 8:00 AM – 4:30 PM",
      distance: "West Bend — Downtown",
      website: "https://www.washcowisco.gov",
      websiteLabel: "Washington County",
      notes: "County clerk passport acceptance services in the Washington County Courthouse. Convenient downtown location with ample parking.",
      icon: "ri-government-line",
    },
    {
      id: "wb-3",
      name: "Germantown Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "W156 N11329 Pilgrim Rd",
      city: "Germantown",
      state: "WI",
      zip: "53022",
      phone: "(262) 251-6656",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Germantown — 10 miles south",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Germantown and southern Washington County residents. Appointment recommended.",
      icon: "ri-mail-line",
    },
    {
      id: "wb-4",
      name: "Menomonee Falls Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "N88 W16731 Appleton Ave",
      city: "Menomonee Falls",
      state: "WI",
      zip: "53051",
      phone: "(262) 251-3306",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Menomonee Falls — 15 miles south",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Menomonee Falls and southern Washington County residents. Appointment recommended.",
      icon: "ri-mail-line",
    },
    {
      id: "wb-5",
      name: "Hartford Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "34 N Main St",
      city: "Hartford",
      state: "WI",
      zip: "53027",
      phone: "(262) 673-3310",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Hartford — 12 miles west",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Hartford area and northwestern Washington County residents. Call ahead for hours.",
      icon: "ri-mail-line",
    },
    {
      id: "wb-6",
      name: "Slinger Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "100 W Washington St",
      city: "Slinger",
      state: "WI",
      zip: "53086",
      phone: "(262) 644-7007",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Slinger — 7 miles west",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Small-town option for Slinger and western Washington County residents. Call ahead — limited hours may vary.",
      icon: "ri-mail-line",
    },
  ],
  comparisonRows: [
    { feature: "Distance to Chicago Agency", agency: "130 miles — 2 hour 10 minute drive on US-45/I-94, but need appointment", fpc: "We drive for you — no appointment needed" },
    { feature: "Appointment Needed", agency: "Yes — call NPIC, very limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
  ],
  mistakes: [
    {
      icon: "ri-map-pin-line",
      title: "Assuming a Milwaukee Agency Exists",
      description: "Many West Bend residents assume there is a passport agency closer than Chicago — perhaps in Milwaukee. There is no passport agency in Milwaukee. The Chicago Passport Agency at 230 S Dearborn St, Suite 380 is the closest, 130 miles south. This misconception leads travelers to delay, thinking they can 'just go downtown Milwaukee' when no Milwaukee agency exists.",
    },
    {
      icon: "ri-calendar-line",
      title: "Waiting Until Kettle Moraine Summer Season",
      description: "Washington County families often plan international vacations around the Kettle Moraine summer season but wait until May or June to apply for passports. The West Bend Post Office and Washington County Clerk are overwhelmed with applications in late spring. Start your passport process in February or March for summer travel.",
    },
    {
      icon: "ri-suitcase-line",
      title: "Corporate Travelers Confusing Last-Minute Domestic and International Procedures",
      description: "West Bend Mutual Insurance professionals accustomed to last-minute domestic travel often underestimate international passport requirements. Unlike domestic flights that require only a driver's license, international travel demands a passport with 6+ months validity — and corporate last-minute booking doesn't change State Department processing times.",
    },
    {
      icon: "ri-restart-line",
      title: "Not Checking Passport Validity Before Booking International Flights",
      description: "Washington County travelers often book international flights months in advance without checking passport expiration dates. Most countries require 6 months passport validity beyond your return date. If your passport expires within 6 months of travel, airlines may deny boarding.",
    },
    {
      icon: "ri-image-line",
      title: "Using Wisconsin DMV Photos as Passport Photos",
      description: "West Bend residents sometimes try to use Wisconsin driver's license photos or self-taken phone photos for passport applications. Passport photos must be exactly 2x2 inches with a pure white background, neutral expression, and no glasses. Only professional passport photo services meet U.S. Department of State standards.",
    },
    {
      icon: "ri-file-list-line",
      title: "Renewing by Mail When In-Person Is Required",
      description: "Washington County residents with passports that expired more than 5 years ago, were issued under age 16, or have a name change without documentation must use Form DS-11 (in-person application) — not DS-82 (mail-in). Filing the wrong form means your application gets rejected and you start over.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send a prepaid overnight label. Drop at any UPS location in West Bend, Germantown, or Menomonee Falls." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team reviews every document, then couriers to the Chicago Passport Agency — 130 miles south via US-45/I-94." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at the Chicago Passport Agency via our pre-secured courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your West Bend or Washington County address — fully trackable." },
  ],
  faqs: [
    {
      q: "How far is West Bend from the Chicago passport agency?",
      a: "West Bend is approximately 130 miles from the Chicago Passport Agency at 230 S Dearborn St, Suite 380 in the Chicago Loop — about a 2 hour 10 minute drive south via US-45 and I-94. However, the Chicago agency requires pre-scheduled appointments and does not accept walk-ins.",
    },
    {
      q: "Does West Bend, WI have its own passport agency?",
      a: "No. West Bend does not have a passport agency. The closest passport agency is the Chicago Passport Agency at 230 S Dearborn St, Suite 380, about 130 miles south. West Bend has passport acceptance facilities — the West Bend Post Office at 607 E Economy Ave and the Washington County Clerk's office — which can accept your DS-11 application but cannot process or expedite it.",
    },
    {
      q: "Is there a passport agency in Milwaukee?",
      a: "No, there is no passport agency in Milwaukee. Many Wisconsin residents incorrectly assume Milwaukee has a passport agency. The closest passport agency to all of Wisconsin is the Chicago Passport Agency at 230 S Dearborn St, Suite 380. Milwaukee only has passport acceptance facilities — post offices and county clerks that accept applications but cannot expedite processing.",
    },
    {
      q: "Where do West Bend travelers typically fly internationally?",
      a: "West Bend travelers typically fly from Milwaukee Mitchell International Airport (MKE, about 40 miles south) or Chicago O'Hare International Airport (ORD, about 130 miles south). MKE offers limited international service while ORD provides extensive global routes. West Bend's location north of Milwaukee provides easy access to both airports.",
    },
    {
      q: "How fast can I get an emergency passport in West Bend?",
      a: "Emergency processing is typically 24–48 hours for West Bend residents. West Bend's location on the US-45 corridor provides efficient overnight courier routing to Chicago. Standard expedited processing runs 2–9 business days.",
    },
    {
      q: "Where can I get passport photos in West Bend?",
      a: "Walgreens at 806 S Main St and CVS Pharmacy at 1400 S Main St in West Bend offer passport photo services. The UPS Store at 1710 Clarence Ct also provides photos. Always verify your photos meet exact 2x2 inch specifications with a white background.",
    },
    {
      q: "Can the Washington County Clerk's office process my passport?",
      a: "Yes. The Washington County Clerk's office at 432 E Washington St in West Bend accepts passport applications as a passport acceptance facility. They can accept DS-11 applications and verify your documents. Call (262) 335-4301 before visiting to confirm current passport service hours.",
    },
    {
      q: "How do I renew my passport in West Bend?",
      a: "Adult renewals meeting all DS-82 eligibility criteria (16 or older, issued within 15 years, same name) can be done entirely by mail. Fast Passport Center's courier service achieves 2–9 business day turnaround — far faster than self-service USPS (8–11 weeks). If you're not eligible for mail-in renewal, visit the West Bend Post Office or Washington County Clerk for an in-person DS-11 application.",
    },
    {
      q: "Does the West Bend Post Office take walk-in passport appointments?",
      a: "The West Bend Post Office at 607 E Economy Ave accepts passport applications by appointment Monday through Friday from 9:00 AM to 4:00 PM. Walk-ins are generally not accepted — call (262) 334-3981 to schedule. The post office is often busiest during lunch hours and late afternoons.",
    },
    {
      q: "Can Fast Passport Center help Germantown and Menomonee Falls residents too?",
      a: "Absolutely. We serve all of Washington County and surrounding communities including Germantown, Menomonee Falls, Hartford, Jackson, Slinger, Kewaskum, and Richfield. Residents of surrounding communities never need to drive to West Bend or Chicago for service.",
    },
    {
      q: "I work at West Bend Mutual Insurance and need a passport for international business — what's the timeline?",
      a: "West Bend Mutual Insurance professionals heading to international conferences or client meetings should start passport preparation 3–4 months before expected travel. Emergency processing delivers passports in 24–48 hours, but having a valid passport ready avoids last-minute stress when international business travel arises unexpectedly.",
    },
    {
      q: "Is the Washington County Clerk faster than the West Bend Post Office for passport applications?",
      a: "Both the West Bend Post Office and the Washington County Clerk's office serve as passport acceptance facilities with similar processing capabilities. The County Clerk's office at 432 E Washington St often has shorter wait times and more flexible hours (8:00 AM–4:30 PM vs. the post office's 9:00 AM–4:00 PM). However, neither facility can expedite processing — that's where Fast Passport Center's courier service comes in.",
    },
  ],
  photoLocations: [
    { name: "Walgreens — West Bend", address: "806 S Main St, West Bend, WI 53095", hours: "Mon–Sun: 7AM–10PM" },
    { name: "CVS Pharmacy — West Bend", address: "1400 S Main St, West Bend, WI 53095", hours: "Mon–Sun: 8AM–9PM" },
    { name: "UPS Store — West Bend", address: "1710 Clarence Ct, West Bend, WI 53095", hours: "Mon–Fri: 8AM–6PM, Sat: 9AM–2PM" },
    { name: "Walgreens — Germantown", address: "N112 W16228 Mequon Rd, Germantown, WI 53022", hours: "Mon–Sun: 7AM–10PM" },
    { name: "CVS Pharmacy — Menomonee Falls", address: "N95 W18373 County Line Rd, Menomonee Falls, WI 53051", hours: "Mon–Sun: 8AM–9PM" },
    { name: "Walmart Photo — Hartford", address: "1225 E Sumner St, Hartford, WI 53027", hours: "Mon–Sun: 7AM–9PM" },
  ],
  dmvLocations: [
    { name: "WI DMV — West Bend", address: "801 W Paradise Dr, West Bend, WI 53095", hours: "Mon–Fri: 7:00AM–5:00PM", note: "Full-service DMV center for Washington County. REAL ID, driver's license, and state ID services. Convenient Paradise Drive location near US-45.", featured: true },
    { name: "WI DMV — Menomonee Falls", address: "N96 W18221 County Line Rd, Menomonee Falls, WI 53051", hours: "Mon–Fri: 7:00AM–5:00PM", note: "Serves southern Washington County and northern Waukesha County residents. About 12 miles south of West Bend.", featured: false },
    { name: "WI DMV — Fond du Lac", address: "51 E 1st St, Fond du Lac, WI 54935", hours: "Mon–Fri: 7:00AM–5:00PM", note: "Convenient for northwestern Washington County residents. About 35 miles north of West Bend.", featured: false },
  ],
  airports: [
    {
      code: "MKE",
      name: "Milwaukee Mitchell International Airport",
      distance: "40 miles south of West Bend",
      address: "5300 S Howell Ave, Milwaukee, WI 53207",
      note: "Wisconsin's busiest airport and closest commercial airport to West Bend. Domestic routes with some international service. About 45 minutes south via US-45 and I-43.",
      flightUrl: "https://www.mitchellairport.com/",
      flightLabel: "MKE Airport",
      featured: true,
    },
    {
      code: "ORD",
      name: "Chicago O'Hare International Airport",
      distance: "130 miles south of West Bend",
      address: "10000 W O'Hare Ave, Chicago, IL 60666",
      note: "Major international gateway with extensive global routes. About 2 hours 10 minutes south. Co-located near Chicago Passport Agency.",
      flightUrl: "https://www.flychicago.com/ohare/",
      flightLabel: "ORD Airport",
      featured: false,
    },
    {
      code: "MDW",
      name: "Chicago Midway International Airport",
      distance: "140 miles south of West Bend",
      address: "5700 S Cicero Ave, Chicago, IL 60638",
      note: "Secondary Chicago airport with domestic and some international routes. About 2 hours 20 minutes south via I-94.",
      flightUrl: "https://www.flychicago.com/midway/",
      flightLabel: "MDW Airport",
      featured: false,
    },
    {
      code: "ATW",
      name: "Appleton International Airport",
      distance: "75 miles north of West Bend",
      address: "W6390 Challenger Dr, Appleton, WI 54914",
      note: "Regional airport with domestic routes via major carriers. About 1 hour 20 minutes north. Alternative for northern Washington County residents.",
      flightUrl: "https://atwairport.com/",
      flightLabel: "ATW Airport",
      featured: false,
    },
  ],
};